const COMPACT_UUID = /^[0-9a-f]{32}$/i;
const HYPHENATED_UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const UUID_TOKEN =
  /(^|[^0-9a-f])([0-9a-f]{32}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?=$|[^0-9a-f])/gi;

export const OFFLINE_UUID_MIGRATION_CONFLICT = "UUID_MIGRATION_DUPLICATE_CONFLICT";
const OFFLINE_UUID_MIGRATION_CONFLICT_MESSAGE =
  "Conflicting offline changes share the same UUID. The newest unsynced change was preserved and requires review.";

export type OfflineUuidStoreName =
  | "requisitionDrafts"
  | "requisitionQueue"
  | "syncMetadata"
  | "cachedRequisitions"
  | "cachedPurchaseOrders"
  | "cachedVendors"
  | "cachedItemCatalog"
  | "cachedComponentTree"
  | "cachedLubeOilChart"
  | "syncEvents"
  | "userSession";

function formatUuid(compact: string): string {
  const normalized = compact.toLowerCase();
  return `${normalized.slice(0, 8)}-${normalized.slice(8, 12)}-${normalized.slice(12, 16)}-${normalized.slice(16, 20)}-${normalized.slice(20)}`;
}

export function normalizeUuid(value: string): string {
  const trimmed = value.trim();
  if (COMPACT_UUID.test(trimmed)) {
    return formatUuid(trimmed);
  }
  if (HYPHENATED_UUID.test(trimmed)) {
    return trimmed.toLowerCase();
  }
  return value;
}

export function normalizeUuidTokens(value: string): string {
  return value.replace(UUID_TOKEN, (_match, prefix: string, token: string) => {
    const compact = token.replace(/-/g, "");
    return `${prefix}${formatUuid(compact)}`;
  });
}

function isIdentifierField(fieldName: string): boolean {
  const normalized = fieldName.toLowerCase();
  return (
    normalized === "id" ||
    normalized === "key" ||
    normalized.endsWith("_id") ||
    normalized.endsWith("_ids") ||
    /Ids?$/.test(fieldName)
  );
}

function normalizeIdentifierValue(value: unknown): unknown {
  if (typeof value === "string") {
    return normalizeUuidTokens(value);
  }
  if (Array.isArray(value)) {
    return value.map((item) => (typeof item === "string" ? normalizeUuidTokens(item) : normalizeUuidFields(item)));
  }
  return normalizeUuidFields(value);
}

function normalizeUuidFields(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeUuidFields(item));
  }
  if (!value || typeof value !== "object") {
    return value;
  }
  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) {
    return value;
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [
      key,
      isIdentifierField(key) ? normalizeIdentifierValue(item) : normalizeUuidFields(item),
    ]),
  );
}

export function normalizeOfflineRecord<TRecord>(record: TRecord): TRecord {
  return normalizeUuidFields(record) as TRecord;
}

export function normalizeOfflineStoreKey(key: string): string {
  return normalizeUuidTokens(key);
}

export function getOfflineRecordPrimaryKey(
  storeName: OfflineUuidStoreName,
  record: Record<string, unknown>,
): string {
  if (storeName === "requisitionDrafts") {
    return String(record.localDraftId);
  }
  if (storeName === "syncMetadata" || storeName === "userSession") {
    return String(record.key);
  }
  return String(record.id);
}

function timestamp(record: Record<string, unknown>, fields: string[]): string {
  for (const field of fields) {
    const value = record[field];
    if (typeof value === "string") {
      return value;
    }
  }
  return "";
}

function queueSafetyRank(record: Record<string, unknown>): number {
  if (record.status === "failed" && record.lastErrorCode === OFFLINE_UUID_MIGRATION_CONFLICT) {
    return 5;
  }
  if (record.status === "completed") {
    return 4;
  }
  if (record.status === "failed" && record.lastErrorCode === "REQUISITION_REJECTED_CONFLICT") {
    return 3;
  }
  if (record.status === "replaying") {
    return 2;
  }
  if (record.status === "failed") {
    return 1;
  }
  return 0;
}

function freshnessFields(storeName: OfflineUuidStoreName): string[] {
  if (storeName === "requisitionDrafts" || storeName === "requisitionQueue") {
    return ["updatedAt", "createdAt"];
  }
  if (storeName.startsWith("cached")) {
    return ["lastSyncedAt"];
  }
  if (storeName === "syncEvents") {
    return ["completedAt", "startedAt"];
  }
  if (storeName === "userSession") {
    return ["cachedAt"];
  }
  return [];
}

function stableJson(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableJson(item)).join(",")}]`;
  }
  if (value && typeof value === "object") {
    return `{${Object.entries(value as Record<string, unknown>)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, item]) => `${JSON.stringify(key)}:${stableJson(item)}`)
      .join(",")}}`;
  }
  return JSON.stringify(value);
}

function queueIntentFingerprint(record: Record<string, unknown>): string {
  return stableJson({
    intentType: record.intentType,
    entityType: record.entityType,
    localDraftId: record.localDraftId,
    serverRequisitionId: record.serverRequisitionId,
    vesselId: record.vesselId,
    authorUserId: record.authorUserId,
    eventSequence: record.eventSequence,
    payload: record.payload,
  });
}

function containsOpaqueObject(value: unknown): boolean {
  if (Array.isArray(value)) {
    return value.some((item) => containsOpaqueObject(item));
  }
  if (!value || typeof value !== "object") {
    return false;
  }
  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) {
    return true;
  }
  return Object.values(value as Record<string, unknown>).some((item) => containsOpaqueObject(item));
}

function stripConflictHistory(record: Record<string, unknown>): Record<string, unknown> {
  const { uuidMigrationConflicts: _ignored, ...snapshot } = record;
  return snapshot;
}

function conflictSnapshots(record: Record<string, unknown>): Array<Record<string, unknown>> {
  if (Array.isArray(record.uuidMigrationConflicts) && record.uuidMigrationConflicts.length > 0) {
    return record.uuidMigrationConflicts
      .filter((snapshot): snapshot is Record<string, unknown> => Boolean(snapshot && typeof snapshot === "object"))
      .map((snapshot) => stripConflictHistory(snapshot));
  }
  return [stripConflictHistory(record)];
}

function conflictRecoverySnapshots(
  current: Record<string, unknown>,
  candidate: Record<string, unknown>,
): Array<Record<string, unknown>> {
  const unique = new Map<string, Record<string, unknown>>();
  for (const snapshot of [...conflictSnapshots(current), ...conflictSnapshots(candidate)]) {
    unique.set(stableJson(snapshot), snapshot);
  }
  return Array.from(unique.values())
    .sort((left, right) => {
      const timestampDifference = timestamp(left, ["updatedAt", "createdAt"]).localeCompare(
        timestamp(right, ["updatedAt", "createdAt"]),
      );
      return timestampDifference || stableJson(left).localeCompare(stableJson(right));
    });
}

function preferFresher(
  storeName: OfflineUuidStoreName,
  current: Record<string, unknown>,
  candidate: Record<string, unknown>,
): Record<string, unknown> {
  const fields = freshnessFields(storeName);
  const currentTimestamp = timestamp(current, fields);
  const candidateTimestamp = timestamp(candidate, fields);
  if (candidateTimestamp !== currentTimestamp) {
    return candidateTimestamp > currentTimestamp ? candidate : current;
  }

  return stableJson(candidate).localeCompare(stableJson(current)) > 0 ? candidate : current;
}

function preferCandidate(
  storeName: OfflineUuidStoreName,
  current: Record<string, unknown>,
  candidate: Record<string, unknown>,
): Record<string, unknown> {
  if (storeName === "requisitionQueue") {
    const hasUnverifiableOpaquePayload =
      containsOpaqueObject(current.payload) || containsOpaqueObject(candidate.payload);
    if (hasUnverifiableOpaquePayload || queueIntentFingerprint(current) !== queueIntentFingerprint(candidate)) {
      const unsynced = [current, candidate].filter((record) => record.status !== "completed");
      const preserved = unsynced.length === 0
        ? preferFresher(storeName, current, candidate)
        : unsynced.reduce((winner, record) => preferFresher(storeName, winner, record));
      return {
        ...preserved,
        status: "failed",
        lastErrorCode: OFFLINE_UUID_MIGRATION_CONFLICT,
        lastError: OFFLINE_UUID_MIGRATION_CONFLICT_MESSAGE,
        uuidMigrationConflicts: conflictRecoverySnapshots(current, candidate),
      };
    }

    const rankDifference = queueSafetyRank(candidate) - queueSafetyRank(current);
    if (rankDifference !== 0) {
      return rankDifference > 0 ? candidate : current;
    }
  }

  return preferFresher(storeName, current, candidate);
}

export function collapseOfflineUuidDuplicates<TRecord extends Record<string, unknown>>(
  storeName: OfflineUuidStoreName,
  records: TRecord[],
): TRecord[] {
  const winners = new Map<string, Record<string, unknown>>();

  for (const originalRecord of records) {
    const normalizedRecord = normalizeOfflineRecord(originalRecord) as Record<string, unknown>;
    const key = getOfflineRecordPrimaryKey(storeName, normalizedRecord);
    const current = winners.get(key);
    winners.set(key, current ? preferCandidate(storeName, current, normalizedRecord) : normalizedRecord);
  }

  return Array.from(winners.entries())
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([, record]) => record as TRecord);
}
